/**
 * sl-yphr: ELSTER Integration Settings Panel
 *
 * State Machine: loading -> disconnected | connected | error
 * Pattern: workflow engine setting-panel.tsx (adapted for ELSTER — no API key, has Steuernummer)
 */

import {
  Button,
  ConfirmModal,
  IconType,
  Input,
  Skeleton,
  Switch,
  Tooltip,
} from '@affine/component';
import { useNavigateHelper } from '@affine/core/components/hooks/use-navigate-helper';
import { DocsService } from '@affine/core/modules/doc';
import { ExplorerIconService } from '@affine/core/modules/explorer-icon/services/explorer-icon';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { DeleteIcon, InformationIcon, ResetIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useRef, useState } from 'react';

import { IntegrationSettingHeader } from '../setting';
import elsterIconWhiteUrl from './elster-icon-white.assets.svg';
import ElsterLogo from './elster-logo.png';
import * as styles from './styles.css';
import { useWorkspaceEricaSettings } from './use-workspace-erica-settings';

type EricaPanelState = 'loading' | 'disconnected' | 'connected' | 'error';

const BUNDESLAND_CODES: [string, string][] = [
  ['BW', 'Baden-Württemberg'],
  ['BY', 'Bayern'],
  ['BE', 'Berlin'],
  ['BB', 'Brandenburg'],
  ['HB', 'Bremen'],
  ['HH', 'Hamburg'],
  ['HE', 'Hessen'],
  ['MV', 'Mecklenburg-Vorpommern'],
  ['NI', 'Niedersachsen'],
  ['NW', 'Nordrhein-Westfalen'],
  ['RP', 'Rheinland-Pfalz'],
  ['SL', 'Saarland'],
  ['SN', 'Sachsen'],
  ['ST', 'Sachsen-Anhalt'],
  ['SH', 'Schleswig-Holstein'],
  ['TH', 'Thüringen'],
];

export function EricaSettingPanel() {
  const t = useI18n();

  const workspace = useService(WorkspaceService).workspace;
  const permissionService = useService(WorkspacePermissionService);
  const isOwnerOrAdmin = useLiveData(
    permissionService.permission.isOwnerOrAdmin$
  );
  const canEdit = isOwnerOrAdmin === true;

  const explorerIconService = useService(ExplorerIconService);
  const docsService = useService(DocsService);
  const workspaceDocs = useLiveData(docsService.list.docs$);
  const { jumpToPage } = useNavigateHelper();

  const {
    isLoading,
    isSaving,
    isTesting,
    isUploadingCert,
    config,
    saveConfig,
    deleteConfig,
    toggleEnabled,
    toggleTestMode,
    testConnection,
    uploadCert,
    deleteCert,
    provisionDoc,
    assignDoc,
    transferDoc,
    isProvisioning,
  } = useWorkspaceEricaSettings();

  const [steuernummer, setSteuernummer] = useState('');
  const [bundesland, setBundesland] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [certPin, setCertPin] = useState('');
  const [showDeleteCertConfirm, setShowDeleteCertConfirm] = useState(false);
  const [showProductionConfirm, setShowProductionConfirm] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState('');
  const [showAssignMode, setShowAssignMode] = useState(false);
  const [showTransferMode, setShowTransferMode] = useState(false);
  const [transferTargetDocId, setTransferTargetDocId] = useState('');
  const certInputRef = useRef<HTMLInputElement>(null);

  // Available docs for assignment/transfer (non-trash, excludes current ELSTER doc)
  const assignableDocs = useMemo(() => {
    const currentElsterDocId = config?.elsterDocId;
    return workspaceDocs
      .filter(doc => !doc.trash$.value && doc.id !== currentElsterDocId)
      .map(doc => ({ id: doc.id, title: doc.title$.value || 'Untitled' }))
      .sort((a: { title: string }, b: { title: string }) =>
        a.title.localeCompare(b.title)
      );
  }, [workspaceDocs, config?.elsterDocId]);

  // Check if the configured ELSTER doc actually exists and isn't trashed
  const elsterDocExists = useMemo(() => {
    if (!config?.elsterDocId) return false;
    const doc = workspaceDocs.find(d => d.id === config.elsterDocId);
    if (!doc) return false;
    return !doc.trash$.value;
  }, [config?.elsterDocId, workspaceDocs]);

  const panelState: EricaPanelState = useMemo(() => {
    if (isLoading) return 'loading';
    if (!config?.configured) return 'disconnected';
    if (config.configured && !config.lastVerifiedAt) return 'error';
    return 'connected';
  }, [isLoading, config]);

  const handleSave = useCallback(async () => {
    try {
      const result = await testConnection();
      if (!result.success) return;
      await saveConfig(steuernummer || undefined, bundesland || undefined);
      setEditMode(false);
      setSteuernummer('');
      setBundesland('');
    } catch {
      // Error already handled via notify
    }
  }, [steuernummer, bundesland, testConnection, saveConfig]);

  const handleDeleteConfirmed = useCallback(async () => {
    try {
      await deleteConfig();
      setEditMode(false);
      setSteuernummer('');
      setBundesland('');
      setShowDeleteConfirm(false);
    } catch {
      // Error already handled
    }
  }, [deleteConfig]);

  const handleTest = useCallback(async () => {
    await testConnection();
  }, [testConnection]);

  const handleEdit = useCallback(() => {
    setEditMode(true);
    setSteuernummer(config?.steuernummer ?? '');
    setBundesland(config?.bundesland ?? '');
  }, [config]);

  const handleToggleEnabled = useCallback(
    async (checked: boolean) => {
      try {
        await toggleEnabled(checked);
      } catch {
        // Error already handled via notify
      }
    },
    [toggleEnabled]
  );

  const handleToggleTestMode = useCallback(
    async (checked: boolean) => {
      // checked=true → test mode on (safe), checked=false → production (needs confirm)
      if (!checked) {
        setShowProductionConfirm(true);
        return;
      }
      try {
        await toggleTestMode(true);
      } catch {
        // Error already handled via notify
      }
    },
    [toggleTestMode]
  );

  const handleConfirmProduction = useCallback(async () => {
    try {
      await toggleTestMode(false);
      setShowProductionConfirm(false);
    } catch {
      // Error already handled via notify
    }
  }, [toggleTestMode]);

  const handleCancel = useCallback(() => {
    setEditMode(false);
    setSteuernummer('');
    setBundesland('');
  }, []);

  const handleCertFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !certPin) return;
      try {
        await uploadCert(file, certPin);
        setCertPin('');
      } catch {
        // Error handled via notify
      }
      // Reset file input so re-selecting same file triggers change
      if (certInputRef.current) {
        certInputRef.current.value = '';
      }
    },
    [certPin, uploadCert]
  );

  const setElsterDocIcon = useCallback(
    (docId: string) => {
      explorerIconService.setIcon({
        where: 'doc',
        id: docId,
        icon: { type: IconType.Blob, url: elsterIconWhiteUrl },
      });
    },
    [explorerIconService]
  );

  const handleProvisionDoc = useCallback(async () => {
    const docId = await provisionDoc();
    if (docId) setElsterDocIcon(docId);
  }, [provisionDoc, setElsterDocIcon]);

  const handleAssignDoc = useCallback(async () => {
    if (!selectedDocId) return;
    const docId = await assignDoc(selectedDocId);
    if (docId) {
      setElsterDocIcon(docId);
      setShowAssignMode(false);
      setSelectedDocId('');
    }
  }, [selectedDocId, assignDoc, setElsterDocIcon]);

  const handleTransferDoc = useCallback(async () => {
    if (!transferTargetDocId) return;
    const docId = await transferDoc(transferTargetDocId);
    if (docId) {
      setElsterDocIcon(docId);
      setShowTransferMode(false);
      setTransferTargetDocId('');
    }
  }, [transferTargetDocId, transferDoc, setElsterDocIcon]);

  const handleOpenElsterDoc = useCallback(() => {
    if (!config?.elsterDocId) return;
    jumpToPage(workspace.id, config.elsterDocId);
  }, [config?.elsterDocId, workspace.id, jumpToPage]);

  const handleDeleteCertConfirmed = useCallback(async () => {
    try {
      await deleteCert();
      setShowDeleteCertConfirm(false);
    } catch {
      // Error handled via notify
    }
  }, [deleteCert]);

  // LOADING
  if (panelState === 'loading') {
    return (
      <div>
        <IntegrationSettingHeader
          icon={
            <img
              src={ElsterLogo}
              width={24}
              height={24}
              alt="ELSTER"
              style={{ borderRadius: 4 }}
            />
          }
          name={t['com.streamlain.integration.erica.name']()}
          desc={t['com.streamlain.integration.erica.desc']()}
        />
        <Skeleton height={120} />
      </div>
    );
  }

  // DISCONNECTED (member view)
  if (panelState === 'disconnected' && !canEdit) {
    return (
      <div>
        <IntegrationSettingHeader
          icon={
            <img
              src={ElsterLogo}
              width={24}
              height={24}
              alt="ELSTER"
              style={{ borderRadius: 4 }}
            />
          }
          name={t['com.streamlain.integration.erica.name']()}
          desc={t['com.streamlain.integration.erica.desc']()}
        />
        <p className={styles.aboutText}>
          {t['com.streamlain.integration.erica.about']()}
        </p>
        <div className={styles.section}>
          <p className={styles.sectionDescription}>
            {t['com.streamlain.integration.erica.read-only-hint']()}
          </p>
        </div>
      </div>
    );
  }

  // DISCONNECTED or EDIT MODE (admin form)
  if (panelState === 'disconnected' || editMode) {
    return (
      <div>
        <IntegrationSettingHeader
          icon={
            <img
              src={ElsterLogo}
              width={24}
              height={24}
              alt="ELSTER"
              style={{ borderRadius: 4 }}
            />
          }
          name={t['com.streamlain.integration.erica.name']()}
          desc={t['com.streamlain.integration.erica.desc']()}
        />
        <p className={styles.aboutText}>
          {t['com.streamlain.integration.erica.about']()}
        </p>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              {editMode
                ? t['com.streamlain.integration.erica.edit-connection']()
                : t['com.streamlain.integration.erica.connect-to-erica']()}
            </div>
          </div>
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {t['com.streamlain.integration.erica.steuernummer']()}
            </div>
            <Input
              value={steuernummer}
              onChange={setSteuernummer}
              placeholder="19811310010"
              disabled={isSaving || isTesting}
              autoComplete="off"
              name="erica-steuernummer"
            />
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabelRow}>
              <div className={styles.fieldLabel}>
                {t['com.streamlain.integration.erica.bundesland']()}
              </div>
              <Tooltip
                content={
                  <div className={styles.codesGrid}>
                    {BUNDESLAND_CODES.map(([code, name]) => (
                      <div key={code} className={styles.codesRow}>
                        <span className={styles.codesCode}>{code}</span>
                        <span>{name}</span>
                      </div>
                    ))}
                  </div>
                }
              >
                <span className={styles.infoIcon}>
                  <InformationIcon />
                </span>
              </Tooltip>
            </div>
            <Input
              value={bundesland}
              onChange={setBundesland}
              placeholder="BY"
              disabled={isSaving || isTesting}
              autoComplete="off"
              name="erica-bundesland"
            />
          </div>

          <div className={styles.buttonRow}>
            {editMode && (
              <Button variant="secondary" onClick={handleCancel}>
                {t['Cancel']()}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={isSaving || isTesting}
              loading={isSaving || isTesting}
            >
              {isTesting
                ? t['com.streamlain.integration.erica.testing']()
                : t['com.streamlain.integration.erica.save-and-test']()}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ERROR state
  if (panelState === 'error') {
    return (
      <div>
        <IntegrationSettingHeader
          icon={
            <img
              src={ElsterLogo}
              width={24}
              height={24}
              alt="ELSTER"
              style={{ borderRadius: 4 }}
            />
          }
          name={t['com.streamlain.integration.erica.name']()}
          desc={t['com.streamlain.integration.erica.desc']()}
        />
        <p className={styles.aboutText}>
          {t['com.streamlain.integration.erica.about']()}
        </p>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              {t['com.streamlain.integration.erica.connection-status']()}
            </div>
            <span className={styles.statusError}>
              <span className={styles.statusDot} />
              {t['com.streamlain.integration.erica.connection-failed']()}
            </span>
          </div>

          {canEdit && (
            <div className={styles.buttonRow}>
              <Button variant="secondary" onClick={handleEdit}>
                {t['com.streamlain.integration.erica.edit']()}
              </Button>
              <Button
                variant="error"
                prefix={<DeleteIcon />}
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSaving}
              >
                {t['com.streamlain.integration.erica.remove']()}
              </Button>
            </div>
          )}

          {!canEdit && (
            <p className={styles.sectionDescription}>
              {t['com.streamlain.integration.erica.read-only-hint']()}
            </p>
          )}
        </div>

        <ConfirmModal
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title={t['com.streamlain.integration.erica.remove']()}
          description={t[
            'com.streamlain.integration.erica.confirm-disconnect'
          ]()}
          confirmText={t['com.streamlain.integration.erica.remove']()}
          confirmButtonOptions={{ variant: 'error' }}
          onConfirm={handleDeleteConfirmed}
        />
      </div>
    );
  }

  // CONNECTED state
  return (
    <div>
      <IntegrationSettingHeader
        icon={
          <img
            src={ElsterLogo}
            width={24}
            height={24}
            alt="ELSTER"
            style={{ borderRadius: 4 }}
          />
        }
        name={t['com.streamlain.integration.erica.name']()}
        desc={t['com.streamlain.integration.erica.desc']()}
        action={
          canEdit ? (
            <Button
              variant="plain"
              prefix={<ResetIcon />}
              onClick={() => {
                void handleTest();
              }}
              disabled={isTesting}
              loading={isTesting}
            >
              {t['com.streamlain.integration.erica.test-connection']()}
            </Button>
          ) : undefined
        }
      />
      <p className={styles.aboutText}>
        {t['com.streamlain.integration.erica.about']()}
      </p>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            {t['com.streamlain.integration.erica.connection-status']()}
          </div>
          <span className={styles.statusConnected}>
            <span className={styles.statusDot} />
            {t['com.streamlain.integration.erica.connected']()}
          </span>
        </div>

        {canEdit && (
          <div className={styles.toggleRow}>
            <div className={styles.toggleLabel}>
              <div className={styles.sectionTitle}>
                {t['com.streamlain.integration.erica.toggle-label']()}
              </div>
              <div className={styles.sectionDescription}>
                {t['com.streamlain.integration.erica.toggle-hint']()}
              </div>
            </div>
            <Switch
              checked={config?.enabled !== false}
              onChange={(checked: boolean) => {
                void handleToggleEnabled(checked);
              }}
              disabled={isSaving}
            />
          </div>
        )}

        {canEdit && (
          <div className={styles.toggleRow}>
            <div className={styles.toggleLabel}>
              <div className={styles.sectionTitle}>
                {t['com.streamlain.integration.erica.test-mode-label']()}
              </div>
              <div className={styles.sectionDescription}>
                {t['com.streamlain.integration.erica.test-mode-hint']()}
              </div>
            </div>
            <Switch
              checked={config?.testMode !== false}
              onChange={(checked: boolean) => {
                void handleToggleTestMode(checked);
              }}
              disabled={isSaving}
            />
          </div>
        )}

        {config?.steuernummer && (
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {t['com.streamlain.integration.erica.steuernummer']()}
            </div>
            <span className={styles.infoValue}>{config.steuernummer}</span>
          </div>
        )}

        {config?.bundesland && (
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {t['com.streamlain.integration.erica.bundesland']()}
            </div>
            <span className={styles.infoValue}>{config.bundesland}</span>
          </div>
        )}

        {config?.lastVerifiedAt && (
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {t['com.streamlain.integration.erica.last-verified']()}
            </div>
            <span className={styles.infoValue}>
              {new Date(config.lastVerifiedAt).toLocaleString()}
            </span>
          </div>
        )}

        {canEdit && (
          <div className={styles.buttonRow}>
            <Button variant="secondary" onClick={handleEdit}>
              {t['com.streamlain.integration.erica.edit']()}
            </Button>
            <Button
              variant="error"
              prefix={<DeleteIcon />}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSaving}
            >
              {t['com.streamlain.integration.erica.remove']()}
            </Button>
          </div>
        )}

        {!canEdit && (
          <p className={styles.sectionDescription}>
            {t['com.streamlain.integration.erica.read-only-hint']()}
          </p>
        )}
      </div>

      {/* Certificate Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            {t['com.streamlain.integration.erica.certificate']()}
          </div>
          <span
            className={
              config?.hasCert ? styles.statusConnected : styles.statusError
            }
          >
            <span className={styles.statusDot} />
            {config?.hasCert
              ? t['com.streamlain.integration.erica.cert-stored']()
              : t['com.streamlain.integration.erica.cert-none']()}
          </span>
        </div>
        <p className={styles.sectionDescription}>
          {t['com.streamlain.integration.erica.cert-hint']()}
        </p>

        {canEdit && !config?.hasCert && (
          <>
            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabel}>
                {t['com.streamlain.integration.erica.cert-pin']()}
              </div>
              <Input
                type="password"
                value={certPin}
                onChange={setCertPin}
                placeholder={t[
                  'com.streamlain.integration.erica.cert-pin-placeholder'
                ]()}
                disabled={isUploadingCert}
                autoComplete="off"
                name="erica-cert-pin"
              />
            </div>
            <div className={styles.buttonRow}>
              <Button
                variant="primary"
                onClick={() => certInputRef.current?.click()}
                disabled={!certPin || isUploadingCert}
                loading={isUploadingCert}
              >
                {isUploadingCert
                  ? t['com.streamlain.integration.erica.cert-uploading']()
                  : t['com.streamlain.integration.erica.cert-upload']()}
              </Button>
            </div>
            <input
              ref={certInputRef}
              type="file"
              accept=".pfx,.p12"
              className={styles.fileInputHidden}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                void handleCertFileChange(e);
              }}
            />
          </>
        )}

        {canEdit && config?.hasCert && (
          <div className={styles.buttonRow}>
            <Button
              variant="error"
              prefix={<DeleteIcon />}
              onClick={() => setShowDeleteCertConfirm(true)}
              disabled={isUploadingCert}
            >
              {t['com.streamlain.integration.erica.cert-remove']()}
            </Button>
          </div>
        )}

        <ConfirmModal
          open={showDeleteCertConfirm}
          onOpenChange={setShowDeleteCertConfirm}
          title={t['com.streamlain.integration.erica.cert-remove']()}
          description={t[
            'com.streamlain.integration.erica.cert-confirm-remove'
          ]()}
          confirmText={t['com.streamlain.integration.erica.cert-remove']()}
          confirmButtonOptions={{ variant: 'error' }}
          onConfirm={handleDeleteCertConfirmed}
        />
      </div>

      {/* ELSTER Document Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            {t['com.streamlain.integration.erica.elster-doc']()}
          </div>
          <span
            className={
              elsterDocExists ? styles.statusConnected : styles.statusError
            }
          >
            <span className={styles.statusDot} />
            {elsterDocExists
              ? t['com.streamlain.integration.erica.elster-doc-created']()
              : t['com.streamlain.integration.erica.elster-doc-none']()}
          </span>
        </div>
        <p className={styles.sectionDescription}>
          {t['com.streamlain.integration.erica.elster-doc-hint']()}
        </p>

        {canEdit && !elsterDocExists && !showAssignMode && (
          <div className={styles.buttonRow}>
            <Button
              variant="primary"
              onClick={() => {
                void handleProvisionDoc();
              }}
              disabled={isProvisioning}
              loading={isProvisioning}
            >
              {isProvisioning
                ? t['com.streamlain.integration.erica.elster-doc-creating']()
                : t['com.streamlain.integration.erica.elster-doc-create']()}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowAssignMode(true)}
              disabled={isProvisioning}
            >
              {t['com.streamlain.integration.erica.elster-doc-assign']()}
            </Button>
          </div>
        )}

        {canEdit && !elsterDocExists && showAssignMode && (
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {t['com.streamlain.integration.erica.elster-doc-assign-label']()}
            </div>
            <select
              value={selectedDocId}
              onChange={e => setSelectedDocId(e.target.value)}
              disabled={isProvisioning}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--streamlain-border-color)',
                background: 'var(--streamlain-background-primary-color)',
                color: 'var(--streamlain-text-primary-color)',
                fontSize: 14,
              }}
            >
              <option value="">
                {t[
                  'com.streamlain.integration.erica.elster-doc-assign-placeholder'
                ]()}
              </option>
              {assignableDocs.map((doc: { id: string; title: string }) => (
                <option key={doc.id} value={doc.id}>
                  {doc.title}
                </option>
              ))}
            </select>
            <div className={styles.buttonRow}>
              <Button
                variant="primary"
                onClick={() => {
                  void handleAssignDoc();
                }}
                disabled={!selectedDocId || isProvisioning}
                loading={isProvisioning}
              >
                {t['com.streamlain.integration.erica.elster-doc-assign-btn']()}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAssignMode(false);
                  setSelectedDocId('');
                }}
              >
                {t['Cancel']()}
              </Button>
            </div>
          </div>
        )}

        {elsterDocExists && !showTransferMode && (
          <div className={styles.buttonRow}>
            <Button variant="secondary" onClick={handleOpenElsterDoc}>
              {t['com.streamlain.integration.erica.elster-doc-open']()}
            </Button>
            {canEdit && (
              <Button
                variant="secondary"
                onClick={() => setShowTransferMode(true)}
                disabled={isProvisioning}
              >
                {t['com.streamlain.integration.erica.elster-doc-transfer']()}
              </Button>
            )}
          </div>
        )}

        {elsterDocExists && showTransferMode && (
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {t['com.streamlain.integration.erica.elster-doc-assign-label']()}
            </div>
            <p className={styles.sectionDescription}>
              {t[
                'com.streamlain.integration.erica.elster-doc-transfer-confirm'
              ]()}
            </p>
            <select
              value={transferTargetDocId}
              onChange={e => setTransferTargetDocId(e.target.value)}
              disabled={isProvisioning}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--streamlain-border-color)',
                background: 'var(--streamlain-background-primary-color)',
                color: 'var(--streamlain-text-primary-color)',
                fontSize: 14,
              }}
            >
              <option value="">
                {t[
                  'com.streamlain.integration.erica.elster-doc-assign-placeholder'
                ]()}
              </option>
              {assignableDocs.map((doc: { id: string; title: string }) => (
                <option key={doc.id} value={doc.id}>
                  {doc.title}
                </option>
              ))}
            </select>
            <div className={styles.buttonRow}>
              <Button
                variant="error"
                onClick={() => {
                  void handleTransferDoc();
                }}
                disabled={!transferTargetDocId || isProvisioning}
                loading={isProvisioning}
              >
                {isProvisioning
                  ? t[
                      'com.streamlain.integration.erica.elster-doc-transferring'
                    ]()
                  : t[
                      'com.streamlain.integration.erica.elster-doc-transfer-btn'
                    ]()}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowTransferMode(false);
                  setTransferTargetDocId('');
                }}
              >
                {t['Cancel']()}
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={t['com.streamlain.integration.erica.remove']()}
        description={t['com.streamlain.integration.erica.confirm-disconnect']()}
        confirmText={t['com.streamlain.integration.erica.remove']()}
        confirmButtonOptions={{ variant: 'error' }}
        onConfirm={handleDeleteConfirmed}
      />

      <ConfirmModal
        open={showProductionConfirm}
        onOpenChange={setShowProductionConfirm}
        title={t['com.streamlain.integration.erica.confirm-production-title']()}
        description={t[
          'com.streamlain.integration.erica.confirm-production-desc'
        ]()}
        confirmText={t[
          'com.streamlain.integration.erica.confirm-production-btn'
        ]()}
        confirmButtonOptions={{ variant: 'error' }}
        onConfirm={handleConfirmProduction}
      />
    </div>
  );
}
